import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import LoadingScreen from "@/components/ui/loading";
import { Close as CloseIcon, Shield } from "@/components/icons";

import { rolesAndPermissionsApi } from "../../services/roles-and-permissions.api";
import type { Role, UserTitle } from "../../types/roles-and-permissions.types";
import styles from "./AssignByTitleModal.module.scss";

interface AssignByTitleModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: Role | null;
  /** Atama sonrası rol listesindeki "atanan kişi" sayısını tazelemek için. */
  onAssigned?: () => void;
}

/**
 * "X ünvanına sahip olanlara tanımla" — rolü, seçilen ünvanlardaki tüm personele
 * toplu olarak tanımlar.
 *
 * Ünvanlar serbest metin bir alandan (`User.title`, İK senkronu) geliyor; bu
 * yüzden kullanıcıya ünvan YAZDIRILMAZ, mevcut ünvanlar listelenip seçtirilir.
 * Yazım farkı (fazladan boşluk, büyük/küçük harf) aksi halde sessizce boş kümeye
 * düşer ve yönetici "tanımlandı" mesajını görüp hiç kimseye tanımlamamış olurdu.
 *
 * İşlem EKLEMEdir: kişinin mevcut rolleri korunur, rolü zaten olanlar atlanır.
 * Bu yüzden onay metninde "yeni tanımlanacak" sayısı ayrıca gösterilir.
 */
const AssignByTitleModal: React.FC<AssignByTitleModalProps> = ({
  isOpen,
  onClose,
  role,
  onAssigned,
}) => {
  const [titles, setTitles] = useState<UserTitle[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !role) return;
    let cancelled = false;

    setLoading(true);
    setSelected(new Set());
    setSearch("");
    // `roleId` ile sorguluyoruz: her ünvanda rolün zaten kaç kişide tanımlı
    // olduğunu görmek, işlemin gerçekte kaç kişiyi etkileyeceğini gösterir.
    rolesAndPermissionsApi
      .getUserTitles({ roleId: role.id, includeInactive })
      .then((data) => {
        if (!cancelled) setTitles(data);
      })
      .catch(() => {
        if (!cancelled) setTitles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, role, includeInactive]);

  // Arama istemci tarafında: ünvan sayısı kurum genelinde birkaç yüzü geçmiyor
  // ve her tuşta sunucuya gitmek seçili kutuları tazelerken yanıp sönmeye yol açar.
  const visibleTitles = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("tr");
    if (!term) return titles;
    return titles.filter((t) => t.title.toLocaleLowerCase("tr").includes(term));
  }, [titles, search]);

  const selectedTitles = useMemo(
    () => titles.filter((t) => selected.has(t.title)),
    [titles, selected],
  );

  /** Seçilen ünvanlardaki toplam personel ve rolü henüz olmayanların sayısı. */
  const summary = useMemo(() => {
    const total = selectedTitles.reduce((sum, t) => sum + t.userCount, 0);
    const already = selectedTitles.reduce((sum, t) => sum + (t.assignedCount ?? 0), 0);
    return { total, already, pending: total - already };
  }, [selectedTitles]);

  const toggle = (title: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  /** Görünen (aranan) ünvanların tamamını seçer ya da seçimi kaldırır. */
  const toggleAllVisible = () => {
    const allSelected = visibleTitles.length > 0 && visibleTitles.every((t) => selected.has(t.title));
    setSelected((prev) => {
      const next = new Set(prev);
      for (const t of visibleTitles) {
        if (allSelected) next.delete(t.title);
        else next.add(t.title);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!role || selected.size === 0) return;

    setSubmitting(true);
    try {
      const result = await rolesAndPermissionsApi.assignByTitle(role.id, {
        titles: [...selected],
        includeInactive,
      });

      toast.success(
        result.assigned > 0
          ? `${result.assigned} kişiye "${role.roleName}" rolü tanımlandı` +
              (result.alreadyAssigned > 0
                ? ` (${result.alreadyAssigned} kişide zaten vardı).`
                : ".")
          : `Seçilen ünvanlardaki herkeste "${role.roleName}" rolü zaten tanımlıydı.`,
      );
      if (result.unmatchedTitles.length > 0) {
        toast.warning(
          `Şu ünvanlarda personel bulunamadı: ${result.unmatchedTitles.join(", ")}`,
        );
      }

      onAssigned?.();
      onClose();
    } catch {
      // Hata mesajını apiClient global toast'u gösteriyor.
    } finally {
      setSubmitting(false);
    }
  };

  const customHeader = (
    <div className={styles.modalHeader}>
      <div className={styles.modalHeaderLeft}>
        <Shield color="#14181F" height={24} width={24} />
        <div>
          <h3 className={styles.modalTitle}>Ünvana Göre Tanımla</h3>
          <p className={styles.modalSubtitle}>
            “{role?.roleName}” rolü, seçilen ünvanlardaki tüm personele tanımlanır.
          </p>
        </div>
      </div>
      <button aria-label="Kapat" className={styles.closeButton} onClick={onClose} type="button">
        <CloseIcon color="#72808f" height={20} width={20} />
      </button>
    </div>
  );

  const customFooter = (
    <div className={styles.footer}>
      <span className={styles.footerSummary}>
        {selected.size === 0
          ? "Ünvan seçin"
          : `${selected.size} ünvan · ${summary.total} kişi · ${summary.pending} yeni tanımlanacak`}
      </span>
      <div className={styles.footerButtons}>
        <Button color="secondary" disabled={submitting} onClick={onClose} type="button" variant="text">
          Vazgeç
        </Button>
        <Button
          color="primary"
          disabled={submitting || selected.size === 0}
          form="assignByTitleForm"
          loading={submitting}
          type="submit"
          variant="contained"
        >
          Tanımla
        </Button>
      </div>
    </div>
  );

  const allVisibleSelected =
    visibleTitles.length > 0 && visibleTitles.every((t) => selected.has(t.title));

  return (
    <Modal
      customFooter={customFooter}
      customHeader={customHeader}
      formId="assignByTitleForm"
      handleSubmit={handleSubmit}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Ünvana Göre Tanımla"
      withForm
    >
      <div className={styles.content}>
        <div className={styles.controls}>
          <Input
            label="Ünvan ara"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Örn. mühendis"
            value={search}
          />
          <Checkbox
            label="Pasif personeli de kapsa"
            onChange={() => setIncludeInactive((v) => !v)}
            value={includeInactive}
          />
        </div>

        {loading ? (
          <LoadingScreen />
        ) : titles.length === 0 ? (
          <p className={styles.empty}>
            Bu domainde ünvanı tanımlı personel bulunamadı. Ünvanlar İK senkronundan
            gelir; personel kayıtlarında ünvan alanı boşsa bu liste boş kalır.
          </p>
        ) : (
          <>
            <div className={styles.listHeader}>
              <button className={styles.selectAll} onClick={toggleAllVisible} type="button">
                {allVisibleSelected ? "Seçimi kaldır" : "Tümünü seç"}
              </button>
              <span className={styles.listCount}>{visibleTitles.length} ünvan</span>
            </div>

            <ul className={styles.titleList}>
              {visibleTitles.map((item) => {
                // Rolü zaten olanlar atlanacağı için "yeni tanımlanacak" sayısı
                // ayrı gösterilir; aksi halde yönetici 42 kişi bekleyip 5 görür.
                const pending = item.userCount - (item.assignedCount ?? 0);
                return (
                  <li className={styles.titleRow} key={item.title}>
                    <Checkbox
                      label={item.title}
                      onChange={() => toggle(item.title)}
                      value={selected.has(item.title)}
                    />
                    <span className={styles.titleCounts}>
                      {item.userCount} kişi
                      {item.assignedCount ? ` · ${pending} yeni` : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <p className={styles.note}>
          Rol <strong>eklenir</strong>: personelin mevcut rolleri korunur, rolü zaten
          tanımlı olanlar atlanır. Bu ekrandan rol kaldırılamaz.
        </p>
      </div>
    </Modal>
  );
};

export default AssignByTitleModal;
